import type { ReactNode } from 'react'

import { Icon } from 'components/Icon'
import { resolveTagColorClasses } from 'constants/tagColors'
import type { TTag } from 'types'
import { cn } from 'utils/cn'

// AccountManagement 等の select と同じ見た目に揃える。
export const selectClassName =
  'h-11 w-full rounded-2xl border border-border/80 bg-white/80 px-3 text-sm text-ink shadow-sm transition focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/30 disabled:cursor-not-allowed disabled:opacity-60'

export const checkboxClassName =
  'h-4 w-4 rounded border-border text-accent focus:ring-accent/40'

export const ASPECT_RATIO_OPTIONS: ReadonlyArray<{
  value: '1:1' | '16:9'
  label: string
}> = [
  { value: '1:1', label: '1:1' },
  { value: '16:9', label: '16:9' },
]

type AspectRatioToggleProps = {
  name: string
  ariaLabel: string
  value: '1:1' | '16:9'
  onChange: (value: '1:1' | '16:9') => void
  disabled?: boolean
}

export function AspectRatioToggle({
  name,
  ariaLabel,
  value,
  onChange,
  disabled = false,
}: AspectRatioToggleProps) {
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className="inline-flex overflow-hidden rounded-xl border border-border/80"
    >
      {ASPECT_RATIO_OPTIONS.map((option, index) => {
        const checked = option.value === value
        return (
          <label
            key={option.value}
            className={cn(
              'inline-flex cursor-pointer items-center px-3 py-1.5 text-xs font-semibold',
              'transition-[background-color,color]',
              'focus-within:ring-2 focus-within:ring-accent focus-within:ring-offset-1',
              checked ? 'bg-accent-soft text-ink' : 'bg-white text-muted',
              !checked && !disabled && 'hover:bg-surface',
              disabled && 'cursor-not-allowed opacity-60',
              index > 0 && 'border-l border-border/80',
            )}
          >
            <input
              type="radio"
              className="sr-only"
              name={name}
              value={option.value}
              checked={checked}
              disabled={disabled}
              onChange={() => onChange(option.value)}
            />
            <span>{option.label}</span>
          </label>
        )
      })}
    </div>
  )
}

type SectionCardProps = {
  title: string
  description?: string
  badge?: ReactNode
  headingId?: string
  children: ReactNode
}

export function SectionCard({
  title,
  description,
  badge,
  headingId,
  children,
}: SectionCardProps) {
  return (
    <section
      className="grid gap-4 rounded-2xl border border-border/70 bg-white p-5 shadow-sm"
      aria-labelledby={headingId}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="grid gap-1">
          <h2 id={headingId} className="text-sm font-semibold text-ink">
            {title}
          </h2>
          {description ? (
            <p className="text-xs text-muted">{description}</p>
          ) : null}
        </div>
        {badge}
      </div>
      {children}
    </section>
  )
}

type FieldLabelProps = {
  label: ReactNode
  hint?: string
  children: ReactNode
  className?: string
}

// input / select / textarea を1つだけ含むフィールド用。グループには FieldGroup を使う。
export function FieldLabel({ label, hint, children, className }: FieldLabelProps) {
  return (
    <label className={cn('grid gap-2 text-xs font-semibold text-ink', className)}>
      <span>{label}</span>
      {children}
      {hint ? (
        <span className="text-xs font-normal text-muted">{hint}</span>
      ) : null}
    </label>
  )
}

type FieldGroupProps = {
  label: ReactNode
  hint?: string
  children: ReactNode
  className?: string
}

// ラベルを持つが label 要素にできないフィールド群（チップ・ラジオピル等）用。
export function FieldGroup({ label, hint, children, className }: FieldGroupProps) {
  return (
    <div className={cn('grid gap-2 text-xs font-semibold text-ink', className)}>
      <span>{label}</span>
      {children}
      {hint ? (
        <span className="text-xs font-normal text-muted">{hint}</span>
      ) : null}
    </div>
  )
}

type RadioPillOption<T extends string> = {
  value: T
  label: string
}

type RadioPillGroupProps<T extends string> = {
  name: string
  ariaLabel: string
  options: ReadonlyArray<RadioPillOption<T>>
  value: T
  onChange: (value: T) => void
  disabled?: boolean
}

// select の代わりに選択肢を全て見せるピル型ラジオ。TagSelector と同じ
// 「アイコン + ベタ塗り反転」で選択状態を示す（WCAG 1.4.1）。
export function RadioPillGroup<T extends string>({
  name,
  ariaLabel,
  options,
  value,
  onChange,
  disabled = false,
}: RadioPillGroupProps<T>) {
  return (
    <div role="radiogroup" aria-label={ariaLabel} className="flex flex-wrap gap-2">
      {options.map((option) => {
        const checked = option.value === value
        return (
          <label
            key={option.value}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full border-2 px-3.5 py-1.5 text-xs font-semibold',
              'transition-[border-color,background-color,box-shadow]',
              'focus-within:ring-2 focus-within:ring-accent focus-within:ring-offset-1',
              checked
                ? 'border-accent bg-accent-soft text-ink'
                : 'border-border/80 bg-white text-muted',
              disabled
                ? 'cursor-not-allowed opacity-60'
                : cn('cursor-pointer', !checked && 'hover:border-ink/40'),
            )}
          >
            <input
              type="radio"
              className="sr-only"
              name={name}
              value={option.value}
              checked={checked}
              disabled={disabled}
              onChange={() => onChange(option.value)}
            />
            {checked ? (
              <Icon
                icon="checkBold"
                width={12}
                height={12}
                className="shrink-0 text-accent"
              />
            ) : (
              <span
                aria-hidden="true"
                className="h-3 w-3 shrink-0 rounded-full border border-current opacity-50"
              />
            )}
            <span>{option.label}</span>
          </label>
        )
      })}
    </div>
  )
}

type SingleTagPickerProps = {
  tags: readonly TTag[]
  value: number | ''
  onChange: (value: number | '') => void
  ariaLabel: string
  noneLabel?: string
  disabled?: boolean
}

// タグを1つだけ選ぶピッカー。TagSelector（複数選択）と同じ見た目で、
// 「設定しない」ピルを先頭に置いて未選択状態を明示する。
export function SingleTagPicker({
  tags,
  value,
  onChange,
  ariaLabel,
  noneLabel = '設定しない',
  disabled = false,
}: SingleTagPickerProps) {
  const pillClassName = (checked: boolean, colorClassName: string) =>
    cn(
      'inline-flex items-center gap-1.5 rounded-full border-2 px-2.5 py-1 text-xs font-semibold',
      'transition-[border-color,box-shadow]',
      'focus-within:ring-2 focus-within:ring-accent focus-within:ring-offset-1',
      colorClassName,
      disabled
        ? 'cursor-not-allowed opacity-60'
        : cn('cursor-pointer', !checked && 'hover:border-ink/40'),
    )

  return (
    <div role="radiogroup" aria-label={ariaLabel} className="flex flex-wrap gap-2">
      <label
        className={pillClassName(
          value === '',
          value === ''
            ? 'border-transparent bg-ink/70 text-white shadow-sm'
            : 'border-border/80 bg-white text-muted',
        )}
      >
        <input
          type="radio"
          className="sr-only"
          checked={value === ''}
          disabled={disabled}
          onChange={() => onChange('')}
        />
        {value === '' ? (
          <Icon icon="checkBold" width={12} height={12} className="shrink-0" />
        ) : (
          <span
            aria-hidden="true"
            className="h-3 w-3 shrink-0 rounded-full border border-current opacity-50"
          />
        )}
        <span>{noneLabel}</span>
      </label>
      {tags.map((tag) => {
        const checked = value === tag.id
        const classes = resolveTagColorClasses(tag.color)
        return (
          <label
            key={tag.id}
            className={pillClassName(
              checked,
              checked
                ? cn('border-transparent text-white shadow-sm', classes.solidBg)
                : cn(classes.bg, classes.border, classes.text),
            )}
          >
            <input
              type="radio"
              className="sr-only"
              checked={checked}
              disabled={disabled}
              onChange={() => onChange(tag.id)}
            />
            {checked ? (
              <Icon
                icon="checkBold"
                width={12}
                height={12}
                className="shrink-0"
              />
            ) : (
              <span
                aria-hidden="true"
                className="h-3 w-3 shrink-0 rounded-full border border-current opacity-50"
              />
            )}
            <span>{tag.name}</span>
          </label>
        )
      })}
    </div>
  )
}
